import { html } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import componentStyles from '../../styles/component.styles.js';
import styles from './button-group.styles.js';
import WebModuleElement from '../../common/webmodule-element.js';
import type { CSSResultGroup } from 'lit';

function findButton(el: HTMLElement) {
  const selector = 'webmodule-button, webmodule-radio-button';

  return el.closest(selector) ?? el.querySelector(selector);
}

/**
 * @summary Groups related buttons into sections.
 *
 * @slot - One or more `<webmodule-button>` elements to display in the button group.
 *
 * @csspart base - The component's base wrapper.
 *
 * @tag webmodule-button-group
 */
export default class WebmoduleButtonGroup extends WebModuleElement {
  static styles: CSSResultGroup = [componentStyles, styles];

  @query('slot') defaultSlot: HTMLSlotElement;
  @state() disableRole = false;
  /**
   * A label to use for the button group. This won't be displayed on the screen, but it will be announced by assistive
   * devices when interacting with the control.
   */
  @property() label = '';

  render() {
    // eslint-disable-next-line lit-a11y/mouse-events-have-key-events
    return html`
      <div
        part="base"
        class="button-group"
        role="${this.disableRole ? 'presentation' : 'group'}"
        aria-label=${this.label}
        @focusout=${this.handleBlur}
        @focusin=${this.handleFocus}
        @mouseover=${this.handleMouseOver}
        @mouseout=${this.handleMouseOut}
      >
        <slot @slotchange=${this.handleSlotChange}></slot>
      </div>
    `;
  }

  private handleFocus(event: Event) {
    const button = findButton(event.target as HTMLElement);
    button?.toggleAttribute('data-webmodule-button-group__button--focus', true);
  }

  private handleBlur(event: Event) {
    const button = findButton(event.target as HTMLElement);
    button?.toggleAttribute('data-webmodule-button-group__button--focus', false);
  }

  private handleMouseOver(event: Event) {
    const button = findButton(event.target as HTMLElement);
    button?.toggleAttribute('data-webmodule-button-group__button--hover', true);
  }

  private handleMouseOut(event: Event) {
    const button = findButton(event.target as HTMLElement);
    button?.toggleAttribute('data-webmodule-button-group__button--hover', false);
  }

  private handleSlotChange() {
    const slottedElements = [...this.defaultSlot.assignedElements({ flatten: true })] as HTMLElement[];

    slottedElements.forEach(el => {
      const index = slottedElements.indexOf(el);
      const button = findButton(el);

      if (button) {
        button.toggleAttribute('data-webmodule-button-group__button', true);
        button.toggleAttribute('data-webmodule-button-group__button--first', index === 0);
        button.toggleAttribute(
          'data-webmodule-button-group__button--inner',
          index > 0 && index < slottedElements.length - 1
        );
        button.toggleAttribute('data-webmodule-button-group__button--last', index === slottedElements.length - 1);
        button.toggleAttribute(
          'data-webmodule-button-group__button--radio',
          button.tagName.toLowerCase() === 'webmodule-radio-button'
        );
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'webmodule-button-group': WebmoduleButtonGroup;
  }
}
